import { Injectable }          from '@angular/core';
import { DataService }         from '@omnilib/services/data.service';
import { LanguageService }     from '@omnilnp/services/language.service';
import { ListService }         from '@omnilib/services/list.service';
import { FormItems, FormItem } from '@omnilnp/interfaces/formitems.interface';
import { RouterService }       from '@omnilnp/services/router.service';
import { add, format, parseISO, toDate  }  from 'date-fns';
import { firstValueFrom } from 'rxjs';
import { PostalService } from './postal.service';

export type AddressLookupResult = 'found' | 'notfound' | 'stale';

@Injectable({
  providedIn: 'root',
})
export class RegisterService {
  formitems: FormItems = {
    initials: { type: 'text', required: true, value: '', error: '', defaultvalue: '' },
    middlename: { type: 'text', required: false, value: '', error: '', defaultvalue: '' },
    lastname: { type: 'text', required: true, value: '', error: '', defaultvalue: '' },
    dateofbirth: { type: 'date', required: true, value: '', error: '', defaultvalue: '' },
    country: {
      type: 'select',
      required: true,
      value: 'NLD',
      error: '',
      defaultvalue: 'NLD'
    },
    zipcode: { type: 'text', required: true, value: '', error: '', defaultvalue: '' },
    street: { type: 'hidden', required: false, visible: false, value: '', error: '', defaultvalue: '' },
    housenumber: { type: 'text', required: true, value: '', error: '', defaultvalue: '' },
    housenumber_suffix: { type: 'text', required: false, value: '', error: '', defaultvalue: '' },
    city: { type: 'hidden', required: false, visible: false, value: '', error: '', defaultvalue: '' },
    fulladdress: { type: 'display', required: false, visible: false, value: '', error: '', defaultvalue: '' },
    phonenumber: { type: 'text', required: true, value: '', error: '', defaultvalue: '' },
    emailaddress: { type: 'email', required: true, value: '', error: '', defaultvalue: '' },
    membertype: {
      type: 'select',
      required: true,
      value: '11',
      error: '',
      defaultvalue: '11'
    }
  };

  dutchaddress = {
    zipcode: { type: 'text', required: true, value: '', error: '', defaultvalue: '' },
    street: { type: 'hidden', required: false, visible: false, value: '', error: '', defaultvalue: '' },
    housenumber: { type: 'text', required: true, value: '', error: '', defaultvalue: '' },
    housenumber_suffix: { type: 'text', required: false, value: '', error: '', defaultvalue: '' },
    city: { type: 'hidden', required: false, visible: false, value: '', error: '', defaultvalue: '' },
    fulladdress: { type: 'display', required: false, visible: false, value: '', error: '', defaultvalue: '' },
  };

  constructor ( public data: DataService
              , public language: LanguageService
              , public list: ListService
              , public router: RouterService
              , private postalService: PostalService
              ) {}

  initialize() {
    for ( let item in this.formitems ) {
      this.formitems[item].value = this.formitems[item].defaultvalue;
      this.formitems[item].error = '';
    }

    for ( let item in this.dutchaddress ) {
      this.dutchaddress[item].value = this.dutchaddress[item].defaultvalue;
      this.dutchaddress[item].error = '';
    }
  }

  // Strip all whitespace and uppercase, so '2831 vr' and '2831VR' are treated the same.
  normalizePostcode( value: string ): string {
    return ( value ?? '' ).replace(/\s/g, '').toUpperCase();
  }

  // Display form only: '2831VR' -> '2831 VR'.
  formatPostcode( value: string ): string {
    let postcode = this.normalizePostcode( value );
    return postcode.length == 6 ? postcode.substr(0,4) + ' ' + postcode.substr(4,2) : postcode;
  }

  // One counter per form object. Any clear or new lookup bumps it, so an older in-flight lookup can detect it is stale.
  private lookupSequence = new WeakMap<object, number>();

  private nextLookup( $formitems ): number {
    let sequence = ( this.lookupSequence.get( $formitems ) ?? 0 ) + 1;
    this.lookupSequence.set( $formitems, sequence );
    return sequence;
  }

  private isCurrentLookup( $formitems, sequence: number ): boolean {
    return this.lookupSequence.get( $formitems ) === sequence;
  }

  // Also invalidates any lookup still in flight for this form.
  clearAddress( $formitems, error = '' ) {
    this.nextLookup( $formitems );
    $formitems.street.value      = '';
    $formitems.city.value        = '';
    $formitems.fulladdress.value = '';
    $formitems.fulladdress.error = error;
  }

  buildFullAddress( $formitems ): string {
    if ( !$formitems.street.value || !$formitems.city.value ) {
      return '';
    }

    return $formitems.street.value
         + ' '
         + $formitems.housenumber.value.trim()
         + ( $formitems.housenumber_suffix.value.trim() != '' ? ' ' + $formitems.housenumber_suffix.value.trim() : '' )
         + ', '
         + this.formatPostcode( $formitems.zipcode.value )
         + '  '
         + $formitems.city.value
         ;
  }

  // True only when every address part came back from the postal lookup and no lookup error is pending.
  hasValidAddress( $formitems ): boolean {
    return $formitems.street.value.trim()      != ''
        && $formitems.city.value.trim()        != ''
        && $formitems.zipcode.value.trim()     != ''
        && $formitems.housenumber.value.trim() != ''
        && $formitems.fulladdress.error        == '';
  }

  // Resolves only after the lookup has completed, so callers can rely on the values afterwards.
  // Returns 'stale' when a newer lookup or clear happened meanwhile, or when the input no longer
  // matches what was looked up. A stale result leaves the form untouched.
  async searchAddress( $formitems ): Promise<AddressLookupResult> {
    this.clearAddress( $formitems );
    let sequence = this.nextLookup( $formitems );

    let postalcode  = this.normalizePostcode( $formitems.zipcode.value );
    let housenumber = ( $formitems.housenumber.value ?? '' ).trim();

    let res;
    try {
      res = await firstValueFrom( this.postalService.checkPostal( postalcode, housenumber ) );
    } catch ( e ) {
      console.error( e );
      res = null;
    }

    if ( !this.isCurrentLookup( $formitems, sequence )
      || this.normalizePostcode( $formitems.zipcode.value ) != postalcode
      || ( $formitems.housenumber.value ?? '' ).trim()      != housenumber
       ) {
      return 'stale';
    }

    let address  = res?.result?.resultset;
    let street   = address ? this.list.getValue( address, 'street',   true ) : undefined;
    let city     = address ? this.list.getValue( address, 'city',     true ) : undefined;
    let postcode = address ? this.list.getValue( address, 'postcode', true ) : undefined;

    if ( !address || this.list.getValue( address, 'addressType' ) == undefined || !street || !city || !postcode ) {
      this.clearAddress( $formitems, 'noaddressfound' );
      return 'notfound';
    }

    // Use the values the postal API returns, not what the user typed.
    $formitems.street.value      = String( street ).trim();
    $formitems.city.value        = String( city ).trim();
    $formitems.zipcode.value     = this.normalizePostcode( String( postcode ) );
    $formitems.fulladdress.value = this.buildFullAddress( $formitems );
    $formitems.fulladdress.error = '';
    return 'found';
  }

  async getBorrowerType(borrowerTypeId) {
    let languageCode = '';
    if(this.language.currentLanguage == 'nl') {
      languageCode = 'ned';
    }
    if(this.language.currentLanguage == 'en') {
      languageCode = 'eng';
    }
    let result = await this.data.callapi( 'get', 'borrow-types/' + borrowerTypeId + "/language/" + languageCode, null);
    return result['result']['resultset'];
  }

  async checkuniquelibmember() {

    let params = { "initials"    : this.formitems.initials.value
                 , "lastName"    : this.formitems.lastname.value
                 , "middleName"  : this.formitems.middlename.value
                 , "dateOfBirth" : format(parseISO(this.formitems.dateofbirth.value+'T00:00:00'),'yyyy-MM-dd' )
               };

    let result = await this.data.callapi ( 'post', 'full-person/check-if-unique', params );

    if( result['result']['code'] ) {
        if( result['result']['code'] == '0') {
            return true;
        }
    }
    return false;
  }

  async save( paid: boolean = false ) {


    let params = { 'initials'        : this.formitems.initials.value
              , 'lastNamePrefix'     :    this.formitems.middlename.value
              ,  'lastName'          : this.formitems.lastname.value
              ,  'dateOfBirth'       : format(parseISO(this.formitems.dateofbirth.value+'T00:00:00'),'dd-MM-yyyy')
              ,  'streetName'        : this.formitems.street.value.trim()
              ,  'houseNumber'       : this.formitems.housenumber.value.trim()
              ,  'houseNumberSuffix' : this.formitems.housenumber_suffix.value.trim()
              ,  'postalCode'        : this.formitems.zipcode.value.trim()
              ,  'city'              : this.formitems.city.value.trim()
              ,  'gender'            : 'o'
              ,  'email'             : this.formitems.emailaddress.value
              ,  'entity'            : 'UVA'
              ,  'location'          : 'BIB01'
              ,  'country'           : this.formitems.country.value
              ,  'language'          : this.language.currentLanguage == 'nl' ? 'NED' : 'ENG'
              ,  'phoneNumber'       : this.formitems.phonenumber.value
              ,  'streetNameDutch'       : this.dutchaddress.street.value.trim()
              ,  'houseNumberDutch'      : this.dutchaddress.housenumber.value.trim()
              ,  'houseNumberSuffixDutch': this.dutchaddress.housenumber_suffix.value.trim()
              ,  'postalCodeDutch'       : this.dutchaddress.zipcode.value.trim()
              ,  'cityDutch'             : this.dutchaddress.city.value.trim()
              ,  'borrowerType'      : this.formitems.membertype.value
    };

    let result = await this.data.callapi ( 'put', 'full-person/import', params );

    if (result['result']['code']) {
      if (result['result']['code'] == '0') {
        this.router.success = true;
        this.router.message = 'success_register_text';
      } else {
        this.router.success = false;
        this.router.message = paid ? 'failed_payment_text' : 'failed_register_text';
      }
    } else {
      this.router.success = false;
      this.router.message = paid ? 'failed_payment_text' : 'failed_register_text';
    }

    this.initialize();
  }
}
